import { useEffect, useRef, useState, useCallback } from "react";
import debounce from "lodash.debounce";
import { getBondPrincipalFieldConfig } from "../bondPrincipalFieldConfig";
import {
  PRINCIPAL_PG_NAME_THRESHOLD,
  findBestPgNameMatch,
} from "../bondPrincipalPgNameSimilarity";

/**
 * Debounced PG name similarity dialog state (features/bonds principal steps).
 */
export function useBondPrincipalNameSuggestion({
  kind,
  data,
  updateData,
  professionalGuardians,
  fetchProfessionalGuardianDetails,
}) {
  const { nameSuggestionDebounceMs, logPgNameSuggestion } =
    getBondPrincipalFieldConfig(kind);

  const nameDebounceRefs = useRef([]);
  const [suggestedPG, setSuggestedPG] = useState([]);
  const [suggestionDialogOpen, setSuggestionDialogOpen] = useState([]);
  const [suggestionOptOut, setSuggestionOptOut] = useState([]);

  useEffect(() => {
    const guardians = Array.isArray(data.guardians) ? data.guardians : [];
    if (logPgNameSuggestion) {
      console.log("[VA PG] sync per-guardian arrays", {
        guardiansLength: guardians.length,
      });
    }
    setSuggestedPG((prev) => guardians.map((_, i) => prev[i] || null));
    setSuggestionDialogOpen((prev) => guardians.map((_, i) => prev[i] || false));
    setSuggestionOptOut((prev) => guardians.map((_, i) => prev[i] || false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.guardians?.length]);

  const getDebouncedCheckName = useCallback(
    (idx) => {
      if (!nameDebounceRefs.current[idx]) {
        nameDebounceRefs.current[idx] = debounce((name) => {
          if (suggestionOptOut[idx]) {
            if (logPgNameSuggestion) {
              console.log("[VA PG] opted out; skipping", { idx });
            }
            return;
          }
          const { best, bestScore, clean } = findBestPgNameMatch(name, professionalGuardians);
          if (logPgNameSuggestion) {
            console.log("[VA PG] debounced name", { idx, name, clean });
          }
          if (!clean || clean.length < 4) {
            setSuggestionDialogOpen((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
            if (logPgNameSuggestion) {
              console.log("[VA PG] name too short; closing dialog", { idx, clean });
            }
            return;
          }
          if (logPgNameSuggestion) {
            console.log("[VA PG] best match", {
              idx,
              bestLabel: best?.label,
              bestScore,
              threshold: PRINCIPAL_PG_NAME_THRESHOLD,
            });
          }
          if (best && bestScore >= PRINCIPAL_PG_NAME_THRESHOLD) {
            setSuggestedPG((prev) => {
              const arr = [...prev];
              arr[idx] = best;
              return arr;
            });
            setSuggestionDialogOpen((prev) => {
              const arr = [...prev];
              arr[idx] = true;
              return arr;
            });
            if (logPgNameSuggestion) {
              console.log("[VA PG] opening dialog", {
                idx,
                label: best?.label,
                score: bestScore,
              });
            }
          } else {
            setSuggestionDialogOpen((prev) => {
              const arr = [...prev];
              arr[idx] = false;
              return arr;
            });
            if (logPgNameSuggestion) {
              console.log("[VA PG] not similar enough; keep closed", {
                idx,
                bestLabel: best?.label,
                bestScore,
              });
            }
          }
        }, nameSuggestionDebounceMs);
      }
      return nameDebounceRefs.current[idx];
    },
    [logPgNameSuggestion, nameSuggestionDebounceMs, professionalGuardians, suggestionOptOut]
  );

  const handlePGSuggestionClose = useCallback(
    (idx) => {
      if (logPgNameSuggestion) {
        console.log("[VA PG] closing dialog", { idx });
      }
      setSuggestionDialogOpen((prev) => {
        const arr = [...prev];
        arr[idx] = false;
        return arr;
      });
    },
    [logPgNameSuggestion]
  );

  const handlePGSuggestionDecline = useCallback(
    (idx) => {
      if (logPgNameSuggestion) {
        console.log("[VA PG] decline suggestion", { idx });
      }
      setSuggestionOptOut((prev) => {
        const arr = [...prev];
        arr[idx] = true;
        return arr;
      });
      handlePGSuggestionClose(idx);
    },
    [handlePGSuggestionClose, logPgNameSuggestion]
  );

  const handlePGSuggestionConfirm = useCallback(
    (idx) => {
      const pg = suggestedPG[idx];
      if (logPgNameSuggestion) {
        console.log("[VA PG] confirm suggestion", {
          idx,
          label: pg?.label,
          value: pg?.value,
        });
      }
      if (!pg) return handlePGSuggestionClose(idx);
      const guardians = Array.isArray(data.guardians) ? [...data.guardians] : [];
      guardians[idx] = {
        ...guardians[idx],
        is_professional_guardian: "Yes",
        professional_guardian: pg.value,
        is_new_pg: false,
        showFullForm: false,
        is_attorney: "No",
        attorney: null,
      };
      updateData({ ...data, guardians });
      fetchProfessionalGuardianDetails(idx, pg.value);
      handlePGSuggestionClose(idx);
    },
    [
      data,
      fetchProfessionalGuardianDetails,
      handlePGSuggestionClose,
      logPgNameSuggestion,
      suggestedPG,
      updateData,
    ]
  );

  const shouldTriggerNameCheck = useCallback(
    (index, guardian) =>
      (guardian.is_professional_guardian !== "Yes" || guardian.showFullForm) &&
      !suggestionOptOut[index],
    [suggestionOptOut]
  );

  return {
    suggestedPG,
    suggestionDialogOpen,
    suggestionOptOut,
    getDebouncedCheckName,
    handlePGSuggestionClose,
    handlePGSuggestionDecline,
    handlePGSuggestionConfirm,
    shouldTriggerNameCheck,
  };
}
